import { Button, CircularProgress, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'
import { filter, includes } from 'lodash'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AssistantShortDTO } from '../../../api/gen/base'
import { useInfiniteAssistantListQuery } from '../../../api/hooks/useInfiniteAssistantListQuery'
import { BlockWrapper } from '../../../components/Wrapper/BlockWrapper'
import { HeaderWrapper } from '../../../components/Wrapper/HeaderWrapper'
import { TitleWrapper } from '../../../components/Wrapper/TitleWrapper'
import { WrapperCenter } from '../../../components/WrapperCenter'
import { AssistantSubscriptionsDialog } from '../../../dialogs/AssistantSubscriptionsDialog'
import { useWrapperStore } from '../../../store/storeWrapper'
import { AssistantSortingField } from '../../../swagger/dto'
import { theme } from '../../../theme'
import { AssistantList } from '../MainPage/components/AssistantList'
import AssistantShopSidebar from './AssistantShopSidebar'

export const AssistantShopPage: FC = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [openAssistantSubscriptionsDialog, setOpenAssistantSubscriptionsDialog] =
        useState<boolean>(false)
    const {
        assistantFiltersList,
        setOpenSidebarType,
        fetchAssistantSubscriptions,
        isDoneUpdateAssistant,
        setIsDoneUpdateAssistant,
    } = useWrapperStore((state) => state)
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()

    const desc = searchParams.get('desc') ?? 'false'
    const order_by = searchParams.get('order_by') ?? AssistantSortingField.Name
    const category_ids = searchParams.getAll('category_ids') ?? []

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchedAfterMount, refetch } =
        useInfiniteAssistantListQuery({
            show_restricted: false,
            category_ids,
            desc,
            order_by,
        })

    useEffect(() => {
        if (isDoneUpdateAssistant) {
            refetch()
            setIsDoneUpdateAssistant(false)
        }
    }, [isDoneUpdateAssistant, refetch])

    useEffect(() => {
        return () => {
            setOpenSidebarType(null)
        }
    }, [])

    useEffect(() => {
        fetchAssistantSubscriptions()
    }, [])

    const selectedCategories = filter(assistantFiltersList.available_categories, ({ id }) =>
        includes(searchParams.getAll('category_ids'), String(id)),
    )

    const cancelCategory = (categoryId: string) => {
        searchParams.delete('category_ids', categoryId)

        setSearchParams(searchParams)
    }

    const assistantList = useMemo(
        () =>
            data?.pages.reduce<AssistantShortDTO[]>((acc, page) => acc.concat(page.objects), []) ??
            [],
        [data],
    )

    const handleLoadMore = useCallback(async () => {
        await fetchNextPage()
    }, [fetchNextPage])

    const handleCloseAssistantSubscriptionsDialog = () => {
        setOpenAssistantSubscriptionsDialog(false)
    }

    const handleOpenAssistantSubscriptionsDialog = () => {
        setOpenAssistantSubscriptionsDialog(true)
    }

    return (
        <BlockWrapper>
            <Grid spacing={3} style={{ padding: '16px' }}>
                <HeaderWrapper
                    sx={{
                        p: 0,
                        mb: 2,
                        border: 'none',
                    }}>
                    <Typography
                        variant="Header3"
                        sx={{
                            color: '#1B1C1D',
                        }}>
                        Все ассистенты
                    </Typography>
                    {isMobile && (
                        <Button
                            sx={{ marginTop: '12px' }}
                            fullWidth={true}
                            onClick={handleOpenAssistantSubscriptionsDialog}
                            variant="outlined">
                            Управление подписками
                        </Button>
                    )}
                    {!isMobile && (
                        <TitleWrapper>
                            <Button
                                fullWidth={true}
                                onClick={handleOpenAssistantSubscriptionsDialog}
                                variant="outlined">
                                Управление подписками
                            </Button>
                        </TitleWrapper>
                    )}
                </HeaderWrapper>
                <AssistantShopSidebar />

                <Grid
                    sx={{
                        overflow: 'auto',
                        height: 'calc(100dvh - 286px)',
                    }}>
                    {isFetching && !isFetchedAfterMount ? (
                        <WrapperCenter>
                            <CircularProgress />
                        </WrapperCenter>
                    ) : (
                        <AssistantList
                            list={assistantList}
                            cols={3}
                            onClickCard={(assistantId: string) =>
                                navigate(`/?assistant_id=${assistantId}`)
                            }
                            loading={isFetching}
                            hasNextPage={hasNextPage}
                            onLoadMore={handleLoadMore}
                        />
                    )}
                </Grid>
            </Grid>
            <AssistantSubscriptionsDialog
                open={openAssistantSubscriptionsDialog}
                handleClose={handleCloseAssistantSubscriptionsDialog}
            />
        </BlockWrapper>
    )
}
